import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.tsx';
import { useState } from 'react';
import { Button, Flex, SimpleGrid, Skeleton, Tooltip } from '@mantine/core';
import { CardBodySkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/CardBodySkeleton.tsx';
import { usePersistedCardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView.ts';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';

import { InvestmentAllocationsCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocationsCards.tsx';
import { InvestmentAllocationsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocationsTable.tsx';
import { InvestmentAllocationsStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocations.styles.tsx';
import { useInvestmentDetailsAllocations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsAllocations.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { useMipSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useMipSecurities.ts';
import { AddInvestmentAllocationDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocationDrawer/AddInvestmentAllocationDrawer.tsx';
import { EditInvestmentAllocationDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentAllocations/InvestmentAllocationDrawer/EditInvestmentAllocationDrawer.tsx';
import { InvestmentDetailsAllocation } from '@dealstack/shared/lib/generated';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { useMutation } from '@tanstack/react-query';
import { removeInvestmentAllocation } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

export const InvestmentAllocations = () => {
	const tMipManage = useTranslationMipManage();
	const { mipId, investmentId } = useRequiredParams('mipId', 'investmentId');
	const [activeView, setActiveView] = usePersistedCardTableView(
		StorageKeys.MIP_INVESTMENT_DETAILS_ALLOCATIONS_VIEW,
		'cards'
	);
	const [addOpen, setAddOpen] = useState(false);
	const [editingAllocation, setEditingAllocation] = useState<InvestmentDetailsAllocation | null>(
		null
	);
	const [deletingAllocation, setDeletingAllocation] =
		useState<InvestmentDetailsAllocation | null>(null);

	const {
		data: allocationsData,
		isLoading: allocationsLoading,
		refetch: refetchAllocations,
	} = useInvestmentDetailsAllocations();
	const { data: summary, refetch: refetchSummary } = useInvestmentDetailsSummary();
	const { data: securities } = useMipSecurities();

	const removeAllocationMutation = useMutation({
		mutationFn: removeInvestmentAllocation,
		onSuccess: () => {
			void refetchAllocations();
			void refetchSummary();
			showToastV2({
				title: tMipManage('investment_allocations.remove.success'),
				state: 'success',
			});
			setDeletingAllocation(null);
		},
	});
	const allocations = allocationsData?.allocations;
	const canMutateAllocations = !!allocationsData?.canMutateAllocations;
	const investmentType = summary?.investmentType;

	const allocatedSecurityIds = new Set((allocations ?? []).map((a) => a.securityId));
	const hasAvailableSecurity =
		!securities || securities.some((s) => !s.deleted && !allocatedSecurityIds.has(s.id));

	const openAdd = () => setAddOpen(true);
	const closeAdd = () => setAddOpen(false);
	const closeEdit = () => setEditingAllocation(null);
	const onDeleteAllocation = (allocation: InvestmentDetailsAllocation | null) => {
		if (allocation) {
			closeEdit();
			setDeletingAllocation(allocation);
		}
	};

	const renderContent = () => {
		if (allocationsLoading) {
			if (activeView === 'cards') {
				return (
					<SimpleGrid cols={3} spacing="0.5rem">
						<Skeleton height={200} radius="sm" />
						<Skeleton height={200} radius="sm" />
						<Skeleton height={200} radius="sm" />
					</SimpleGrid>
				);
			}
			return <CardBodySkeleton count={3} rowHeight={40} mih={200} p={0} />;
		}

		if (!allocations?.length) {
			return (
				<div className="placeholder">
					<InvestmentPlaceholder
						icon="folder-ball"
						label={tMipManage('investment_allocations.placeholder_label')}
						mih={200}
					/>
				</div>
			);
		}

		if (activeView === 'cards') {
			return (
				<InvestmentAllocationsCards
					allocations={allocations}
					investmentType={investmentType}
					onEditAllocation={setEditingAllocation}
					canMutateAllocations={canMutateAllocations}
				/>
			);
		}

		return (
			<div className="table-wrap">
				<InvestmentAllocationsTable
					allocations={allocations}
					investmentType={investmentType}
					onEditAllocation={setEditingAllocation}
					canMutateAllocations={canMutateAllocations}
				/>
			</div>
		);
	};

	const isAddDisabled = !canMutateAllocations || !hasAvailableSecurity;
	let addDisabledTooltip: string | null = null;

	if (!canMutateAllocations) {
		addDisabledTooltip = tMipManage('investment_allocations.frozen_tooltip');
	} else if (!hasAvailableSecurity) {
		addDisabledTooltip = tMipManage('investment_allocations.no_available_security_tooltip');
	}

	const addAllocationButton = (
		<Button variant="default" onClick={openAdd} disabled={isAddDisabled}>
			{tMipManage('investment_allocations.add_allocation_cta')}
		</Button>
	);

	return (
		<>
			<InvestmentDetailsCard
				cardLabel={tMipManage('investment_allocations.card_label')}
				variant="borderless"
				actions={
					<Flex gap="0.5rem" align="center">
						{addDisabledTooltip ? (
							<Tooltip label={addDisabledTooltip}>
								<div>{addAllocationButton}</div>
							</Tooltip>
						) : (
							addAllocationButton
						)}

						<CardTableViewSelector value={activeView} onViewChange={setActiveView} />
					</Flex>
				}
			>
				<InvestmentAllocationsStyles>{renderContent()}</InvestmentAllocationsStyles>
			</InvestmentDetailsCard>

			<AddInvestmentAllocationDrawer
				opened={addOpen}
				onClose={closeAdd}
				existingAllocations={allocations ?? []}
			/>

			<EditInvestmentAllocationDrawer
				opened={!!editingAllocation}
				onClose={closeEdit}
				allocation={editingAllocation}
				existingAllocations={allocations ?? []}
				onDeleteAllocation={onDeleteAllocation}
			/>

			<ConfirmDeleteDrawer
				opened={!!deletingAllocation}
				onClose={() => setDeletingAllocation(null)}
				title={tMipManage('investment_allocations.delete_drawer.title')}
				inputLabel={tMipManage('investment_allocations.delete_drawer.inputLabel')}
				inputPlaceholder={tMipManage(
					'investment_allocations.delete_drawer.inputPlaceholder'
				)}
				hideAlertIcon={true}
				confirmDeleteText="delete"
				wordMismatchErrorMessage={tMipManage(
					'investment_allocations.delete_drawer.wordMismatchError'
				)}
				deleteBtnText={tMipManage('investment_allocations.delete_drawer.confirmDelete')}
				cancelBtnText={tMipManage('investment_allocations.delete_drawer.cancel')}
				deleteLoading={removeAllocationMutation.isPending}
				deleteSuccess={removeAllocationMutation.isSuccess}
				handleDelete={() => {
					if (!deletingAllocation) {
						return;
					}

					removeAllocationMutation.mutate({
						mipId,
						investmentId,
						allocationId: deletingAllocation.id,
					});
				}}
			/>
		</>
	);
};
